import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';

// Workflows do Knock que produzem highlights de reconhecimento de risco. O backend passou de um
// único `risk-recognition-highlight-computed` para workflows por tipo de risco, então aceitamos todos.
export const RISK_RECOGNITION_WORKFLOW_KEYS: ReadonlySet<string> = new Set(['speed-event-risk-recognition', 'fatigue-risk-recognition', 'long-journey-risk-recognition']);

// Janela de "eventos atuais": além do que chega ao vivo pelo socket, o card também ressurge
// eventos disparados nos últimos 10 min (vindos da página buscada na montagem).
export const RECENT_TRIGGER_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

const HIGHLIGHT_DATA_KEYS = [
  'highlight',
  'risk_recognition_highlight',
  'riskRecognitionHighlight',
  'risk_recognition',
  'riskRecognition',
  'highlight_response',
  'highlightResponse',
];

type FeedActionButton = { name?: string; label?: string; action?: string };
type FeedContentBlock = { type?: string; name?: string; buttons?: FeedActionButton[] };

export type RiskRecognitionFeedItem = {
  id: string;
  seen_at: string | null;
  inserted_at?: string;
  source?: { key?: string };
  data: unknown;
  activities?: Array<{ data?: unknown }>;
  // Blocos de conteúdo renderizados pelo Knock; o `button_set` carrega os CTAs (label + action).
  blocks?: FeedContentBlock[];
};

// Sentinela: o backend manda essa "action" no botão primário pra sinalizar "abrir o drawer" (sem ser
// uma navegação de verdade). Comparamos por substring pra tolerar o `?` inicial ir/vir.
export const OPEN_DRAWER_ACTION = 'risk-recognition-drawer=true';

// Param de deep link legado do drawer de risco (`?risk-recognition-drawer=<id>`). Mantido como alias pelo
// host do drawer pra links já compartilhados não quebrarem; tipos novos usam o param genérico.
export const RISK_DRAWER_PARAM = 'risk-recognition-drawer';

// Lê os botões do `button_set` do feed item (labels vindas do workflow Knock).
const getButtonSetButtons = (item: RiskRecognitionFeedItem): FeedActionButton[] => item.blocks?.find((block) => block.type === 'button_set')?.buttons ?? [];

export interface RiskRecognitionFeedButtonLabels {
  action: string | null;
  dismiss: string | null;
}

export const getFeedButtonLabelsFromItem = (item: RiskRecognitionFeedItem): RiskRecognitionFeedButtonLabels => {
  const buttons = getButtonSetButtons(item);
  const primary = buttons.find((button) => button.name === 'primary') ?? buttons.find((button) => button.action);
  const secondary = buttons.find((button) => button.name === 'secondary') ?? buttons.find((button) => button !== primary && button.label);

  return {
    action: primary?.label?.trim() || null,
    dismiss: secondary?.label?.trim() || null,
  };
};

// Lê a `action` do botão primário do button_set (fallback: primeiro botão com action). Null se ausente.
export const getPrimaryActionFromFeedItem = (item: RiskRecognitionFeedItem): string | null => {
  const buttons = getButtonSetButtons(item);
  const primary = buttons.find((button) => button.name === 'primary') ?? buttons.find((button) => button.action);
  return primary?.action || null;
};

// Disparado dentro da janela? (inserted_at = quando o Knock criou a notificação do evento.)
// `windowMs` default é a janela de 10 min; o hook pode passar um override vindo da URL.
export const isRecentlyTriggered = (item: RiskRecognitionFeedItem, nowMs: number, windowMs: number = RECENT_TRIGGER_WINDOW_MS): boolean => {
  const triggeredMs = Date.parse(item.inserted_at ?? '');
  return Number.isFinite(triggeredMs) && nowMs - triggeredMs <= windowMs;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isRiskRecognitionHighlightData = (value: unknown): value is RiskRecognitionHighlight =>
  isRecord(value) &&
  typeof value.time_bucket_begin === 'string' &&
  typeof value.time_bucket_end === 'string' &&
  typeof value.detection_count === 'number' &&
  typeof value.title === 'string' &&
  typeof value.description === 'string';

const findHighlightData = (value: unknown): RiskRecognitionHighlight | null => {
  if (isRiskRecognitionHighlightData(value)) return value;
  if (!isRecord(value)) return null;

  for (const key of HIGHLIGHT_DATA_KEYS) {
    const candidate = findHighlightData(value[key]);
    if (candidate) return candidate;
  }

  return null;
};

export const getHighlightFromFeedItem = (item: RiskRecognitionFeedItem): RiskRecognitionHighlight | null => {
  if (!RISK_RECOGNITION_WORKFLOW_KEYS.has(item.source?.key ?? '')) return null;

  return findHighlightData(item.data) ?? findHighlightData(item.activities?.[0]?.data);
};

// Regras de risco conhecidas (gate de render). O backend pode mandar regras novas antes do front saber
// renderá-las — só mostramos as mapeadas.
export const RISK_RECOGNITION_RULE_NAMES = ['long_journey', 'severe_speed', 'fatigue'] as const;
export type RiskRecognitionRuleName = (typeof RISK_RECOGNITION_RULE_NAMES)[number];

export const isKnownRiskRule = (ruleName: string | undefined): ruleName is RiskRecognitionRuleName =>
  !!ruleName && (RISK_RECOGNITION_RULE_NAMES as readonly string[]).includes(ruleName);

// Aceita `undefined` (o highlight vem do cache da query, `T | undefined`). Gate de render do card/toast:
// tem highlight, tem detecção, é regra conhecida, e não foi dispensado nesta sessão (bucket dispensado).
export const isHighlightRenderable = (highlight: RiskRecognitionHighlight | null | undefined, dismissedBucketEnd: string | null): boolean =>
  !!highlight && highlight.detection_count > 0 && isKnownRiskRule(highlight.metadata?.rule_name) && highlight.time_bucket_end !== dismissedBucketEnd;
